import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/database";
import { Middleware, Store } from "redux";
import * as userActions from "./actions/user";

export const VIEWSPARK_PERMISSION_DENIED = "viewspark-permission-denied";

const RRF_ERROR = "@@reactReduxFirebase/ERROR";
const RRF_UNAUTHORIZED_ERROR = "@@reactReduxFirebase/UNAUTHORIZED_ERROR";

type RecoveryStore = Pick<Store, "getState" | "dispatch">;

let recoveryStarted = false;
let recoveryInFlight = false;
let detectionInstalled = false;
let recoveryStore: RecoveryStore | null = null;

function isClientBrowser(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.addEventListener === "function" &&
    typeof window.location !== "undefined"
  );
}

function isPublicAuthRoute(): boolean {
  const path = window.location.pathname;
  if (path === "/" || path === "/login" || path === "/setup-phone") {
    return true;
  }
  return (
    path.startsWith("/signup") ||
    path.startsWith("/forgot") ||
    path.startsWith("/forgotPassword") ||
    path.startsWith("/dns-setup") ||
    path.startsWith("/invite")
  );
}

function permissionDeniedInText(value: string): boolean {
  const normalized = value.toLowerCase().replace(/_/g, " ");
  return normalized.includes("permission denied");
}

export function isFirebasePermissionDenied(error: unknown): boolean {
  if (!error) {
    return false;
  }
  if (typeof error === "string") {
    return permissionDeniedInText(error);
  }
  if (typeof error !== "object") {
    return false;
  }
  const record = error as Record<string, unknown>;
  if ("code" in record && record.code != null && permissionDeniedInText(String(record.code))) {
    return true;
  }
  if ("message" in record && record.message != null && permissionDeniedInText(String(record.message))) {
    return true;
  }
  return permissionDeniedInText(String(error));
}

function canStartRecovery(): boolean {
  if (!isClientBrowser() || recoveryStarted) {
    return false;
  }
  if (isPublicAuthRoute()) {
    return false;
  }
  recoveryStarted = true;
  return true;
}

function inspectRrfDispatchAction(action: unknown): void {
  if (!action || typeof action !== "object") {
    return;
  }
  const record = action as { type?: string; payload?: unknown };
  if (record.type === RRF_ERROR || record.type === RRF_UNAUTHORIZED_ERROR) {
    notifyFirebasePermissionDenied(record.payload);
  }
}

function wrapStoreDispatch(store: RecoveryStore): void {
  const marker = "__vsDispatchWrapped";
  const current = store.dispatch as unknown as Record<string, boolean>;
  if (current[marker]) {
    return;
  }
  const inner = store.dispatch.bind(store);
  const wrapped = ((action: unknown) => {
    const result = inner(action);
    inspectRrfDispatchAction(action);
    return result;
  }) as typeof store.dispatch;
  (wrapped as unknown as Record<string, boolean>)[marker] = true;
  store.dispatch = wrapped;
}

export function registerFirebasePermissionDeniedStore(store: RecoveryStore): void {
  recoveryStore = store;
  wrapStoreDispatch(store);
}

export function dispatchFirebasePermissionDenied(): void {
  if (!canStartRecovery()) {
    return;
  }
  recoverFromFirebasePermissionDenied();
}

export function recoverFromFirebasePermissionDenied(): void {
  if (recoveryInFlight) {
    return;
  }
  if (!recoveryStarted && !canStartRecovery()) {
    return;
  }
  recoveryInFlight = true;
  recoveryStarted = true;

  const store = recoveryStore;
  if (store) {
    const remember = (store.getState() as { user?: { remember?: boolean } })?.user?.remember;
    store.dispatch(userActions.logoutCurrentUser(remember === true));
  }

  window.location.href = "/login?session=expired";

  void firebase.auth().signOut().catch(() => {});
  const firebaseApi = firebase as unknown as { logout?: () => Promise<void> };
  if (typeof firebaseApi.logout === "function") {
    void firebaseApi.logout().catch(() => {});
  }
}

function notifyFirebasePermissionDenied(error: unknown): void {
  if (!isFirebasePermissionDenied(error)) {
    return;
  }
  if (!firebase.auth().currentUser) {
    return;
  }
  dispatchFirebasePermissionDenied();
}

function patchRtdbReferenceOn(refProto: firebase.database.Reference): void {
  const marker = "__vsPermissionDeniedOnPatched";
  if ((refProto as unknown as Record<string, boolean>)[marker]) {
    return;
  }
  (refProto as unknown as Record<string, boolean>)[marker] = true;

  const originalOn = refProto.on;
  refProto.on = function onWithPermissionDenied(
    eventType: firebase.database.EventType,
    callback: (a: firebase.database.DataSnapshot, b?: string | null) => unknown,
    cancelCallbackOrContext?: ((error: Error) => void) | object | null,
    context?: object | null
  ) {
    const wrappedCancel = (error: Error) => {
      notifyFirebasePermissionDenied(error);
      if (typeof cancelCallbackOrContext === "function") {
        cancelCallbackOrContext.call(context, error);
      }
    };
    if (typeof cancelCallbackOrContext === "function") {
      return originalOn.call(this, eventType, callback, wrappedCancel, context);
    }
    return originalOn.call(this, eventType, callback, wrappedCancel);
  };
}

function patchRtdbReferenceOnce(refProto: firebase.database.Reference): void {
  const marker = "__vsPermissionDeniedOncePatched";
  if ((refProto as unknown as Record<string, boolean>)[marker]) {
    return;
  }
  (refProto as unknown as Record<string, boolean>)[marker] = true;

  const originalOnce = refProto.once;
  refProto.once = function onceWithPermissionDenied(
    eventType: firebase.database.EventType,
    successCallback?: (a: firebase.database.DataSnapshot, b?: string | null) => unknown,
    failureCallbackOrContext?: ((error: Error) => void) | object | null,
    context?: object | null
  ) {
    if (typeof successCallback === "function") {
      const wrappedFailure = (error: Error) => {
        notifyFirebasePermissionDenied(error);
        if (typeof failureCallbackOrContext === "function") {
          failureCallbackOrContext.call(context, error);
        }
      };
      return originalOnce.call(this, eventType, successCallback, wrappedFailure, context);
    }
    return originalOnce.call(this, eventType);
  };
}

function patchRtdbPrototypes(): void {
  if (typeof firebase.database !== "function") {
    return;
  }
  const refProto = firebase.database.Reference.prototype;
  patchRtdbReferenceOn(refProto);
  patchRtdbReferenceOnce(refProto);
  const queryProto = (firebase.database as unknown as { Query?: { prototype: firebase.database.Reference } })
    .Query?.prototype;
  if (queryProto && queryProto !== refProto) {
    patchRtdbReferenceOn(queryProto);
    patchRtdbReferenceOnce(queryProto);
  }
}

export function firebasePermissionDeniedMiddleware(): Middleware {
  return () =>
    next =>
    action => {
      const result = next(action);
      inspectRrfDispatchAction(action);
      return result;
    };
}

export function installFirebasePermissionDeniedDetection(): void {
  if (!isClientBrowser() || detectionInstalled) {
    return;
  }
  detectionInstalled = true;
  patchRtdbPrototypes();

  window.addEventListener("unhandledrejection", event => {
    notifyFirebasePermissionDenied(event.reason);
  });
}

if (__DEV__) {
  if (!isFirebasePermissionDenied({ code: "PERMISSION_DENIED" })) {
    console.error("firebasePermissionDenied: PERMISSION_DENIED");
  }
  if (
    !isFirebasePermissionDenied(
      new Error("permission_denied at /charity/segments")
    )
  ) {
    console.error("firebasePermissionDenied: permission_denied message");
  }
  if (!!isFirebasePermissionDenied({ code: "storage/object-not-found" })) {
    console.error("firebasePermissionDenied: non-permission code");
  }
}
